import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { normalizeForComparison } from '../../../shared/utils/text-normalization.utils';
import { TicketsApiService } from '../services/tickets-api.service';
import { TicketsConfigApiService } from '../services/tickets-config-api.service';
import { TicketsDrawerService } from '../services/tickets-drawer.service';
import {
  Ticket,
  TicketKanbanCardResponse,
  TicketKanbanColumnResponse
} from '../models/tickets.model';
import {
  TicketCategory,
  TicketPipeline,
  TicketPipelineStage,
  TicketPriority,
  TicketStatus
} from '../models/tickets-config.model';
import { TicketsDetailModalComponent } from '../tickets-detail-modal/tickets-detail-modal.component';

interface KanbanColumn extends TicketKanbanColumnResponse {
  cards: TicketKanbanCardResponse[];
}

@Component({
  selector: 'app-tickets-kanban',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    DragDropModule,
    TicketsDetailModalComponent,
    HasPermissionDirective
  ],
  templateUrl: './tickets-kanban.component.html',
  styleUrl: './tickets-kanban.component.css'
})
export class TicketsKanbanComponent implements OnInit, OnDestroy {
  loading = false;
  error = '';

  // Pipelines + selección
  pipelines: TicketPipeline[] = [];
  selectedPipelineId: number | null = null;

  // Columnas reales del backend
  columns: KanbanColumn[] = [];

  // Catálogos para mapear ids → labels en la card
  priorities: TicketPriority[] = [];
  statuses: TicketStatus[] = [];
  categories: TicketCategory[] = [];
  stages: TicketPipelineStage[] = []; // de la pipeline seleccionada

  // Filtros UI (cliente)
  showFilters = false;
  /**
   * Texto libre. Filtra las tarjetas YA cargadas: el endpoint del kanban no acepta
   * búsqueda y trae hasta 50 tarjetas por columna, así que en un pipeline con más
   * tickets que eso la vista de lista es la que busca de verdad.
   */
  filtroTexto = '';
  filtroIdPriority: number | undefined;
  filtroIdCategory: number | undefined;

  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_tickets';
  filtroOwner: 'all' | 'mine' =
    localStorage.getItem(this.OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all';

  // Modal detalle (carga el Ticket completo al abrir)
  showDetailModal = false;
  selectedTicket: Ticket | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected router: Router,
    private ticketsApi: TicketsApiService,
    private configApi: TicketsConfigApiService,
    private drawerService: TicketsDrawerService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.cargarCatalogosYPipelines();

    // Inc 63: recargar el tablero cuando se guarda/cierra un ticket desde el drawer
    // (p.ej. cambiar de etapa/estado o cerrar), igual que ya lo hace la lista.
    // El backend deja `idStage` en la etapa correcta, así que basta con recargar.
    this.drawerService.ticketSaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargarKanban());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Bootstrap =====
  private cargarCatalogosYPipelines(): void {
    this.loading = true;
    forkJoin({
      pipelines: this.configApi.getPipelines().pipe(catchError(() => of([] as TicketPipeline[]))),
      defaultPipeline: this.configApi
        .getDefaultPipeline()
        .pipe(catchError(() => of(null as TicketPipeline | null))),
      priorities: this.configApi.getPriorities().pipe(catchError(() => of([] as TicketPriority[]))),
      statuses: this.configApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[]))),
      categories: this.configApi.getCategories().pipe(catchError(() => of([] as TicketCategory[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pipelines, defaultPipeline, priorities, statuses, categories }) => {
        this.pipelines = pipelines;
        this.priorities = priorities;
        this.statuses = statuses;
        this.categories = categories;

        const initial =
          defaultPipeline?.id ?? pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]?.id ?? null;
        if (initial != null) {
          this.selectedPipelineId = initial;
          this.cargarKanban();
        } else {
          this.loading = false;
          this.error = 'No hay pipelines configurados.';
        }
      });
  }

  onChangePipeline(pipelineId: number): void {
    this.selectedPipelineId = Number(pipelineId);
    this.cargarKanban();
  }

  cargarKanban(): void {
    if (this.selectedPipelineId == null) return;
    this.loading = true;
    this.error = '';

    forkJoin({
      kanban: this.ticketsApi
        .getKanban(this.selectedPipelineId)
        .pipe(catchError(() => of([] as TicketKanbanColumnResponse[]))),
      stages: this.configApi
        .getPipelineStages(this.selectedPipelineId)
        .pipe(catchError(() => of([] as TicketPipelineStage[])))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ kanban, stages }) => {
        this.stages = stages;
        this.columns = kanban
          .map<KanbanColumn>((c) => ({ ...c, cards: c.cards ?? [] }))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.applyFilters();
        this.loading = false;
      });
  }

  // ===== Filtros UI =====
  buscar(): void {
    this.applyFilters();
  }

  setOwnerFilter(value: 'all' | 'mine'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.applyFilters();
  }

  limpiarFiltro(): void {
    this.filtroOwner = 'all';
    localStorage.setItem(this.OWNER_FILTER_KEY, 'all');
    this.filtroTexto = '';
    this.filtroIdPriority = undefined;
    this.filtroIdCategory = undefined;
    this.applyFilters();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  /**
   * Todo lo que la tarjeta enseña, en un solo texto: número, asunto, responsable, contacto
   * y empresa. Buscar solo por el asunto dejaba fuera justo lo que la gente teclea — el
   * número del ticket o el nombre del cliente —, y eso ya viene resuelto en la tarjeta.
   * Sin acentos, porque «Maria» y «María» son la misma persona para quien busca.
   */
  private textoBuscable(card: TicketKanbanCardResponse): string {
    return normalizeForComparison([
      card.ticketNumber,
      card.subject,
      card.ownerUserName,
      card.contactName,
      card.companyName
    ].filter((parte): parte is string => !!parte).join(' '));
  }

  /** Filtra las cards en cliente. El backend ya filtra por pipeline. */
  private applyFilters(): void {
    const term = normalizeForComparison(this.filtroTexto ?? '');
    this.columns = this.columns.map((col) => {
      const filtered = (col.cards ?? []).filter((card) => {
        if (term && !this.textoBuscable(card).includes(term)) return false;
        if (this.filtroIdPriority !== undefined && card.idTicketPriority !== this.filtroIdPriority)
          return false;
        // Owner mine: requiere user actual; placeholder usa intIdUserOwner == null check
        return true;
      });
      return { ...col, cards: filtered };
    });
  }

  // ===== Drag & Drop =====
  drop(event: CdkDragDrop<TicketKanbanCardResponse[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    // El id de columna viene como 'col-<stageId>'
    const targetColumnId = event.container.id.replace(/^col-/, '');
    const newStageId = Number(targetColumnId);
    const card = event.container.data[event.currentIndex];
    if (!card?.id || Number.isNaN(newStageId)) return;

    // PATCH al ticket. Sin toast — la skill toast indica que drag-drop es self-evident.
    this.ticketsApi
      .updateTicket(card.id, { idStage: newStageId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => {
          // Revertir UI: recargar kanban
          this.toast.error({
            title: 'No se pudo mover el ticket',
            message: err.error?.message || 'Error al actualizar la etapa'
          });
          this.cargarKanban();
        }
      });
  }

  getColumnIds(): string[] {
    return this.columns.map((c) => 'col-' + c.stageId);
  }

  // ===== Helpers UI =====
  getPriorityLabel(idPriority: number | null): string {
    if (idPriority == null) return '-';
    return this.priorities.find((p) => p.id === idPriority)?.label || '-';
  }

  getPriorityColor(idPriority: number | null): string {
    if (idPriority == null) return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    const level = this.priorities.find((p) => p.id === idPriority)?.level;
    if (level === 1 || level === 2)
      return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
    if (level === 3) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
    if (level === 4) return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }

  /**
   * Color de estado del card. Sin SLA tracking aún (Fase 3) — placeholder con `firstResponseAt`.
   */
  getStageDotColor(column: KanbanColumn): string {
    if (column.isClosedStage) return 'bg-green-500';
    if (column.stageColor) return ''; // se aplica como style inline si existe
    return 'bg-blue-500';
  }

  // ===== Acciones =====
  vistaLista(): void {
    this.router.navigate(['/tickets']);
  }

  nuevoTicket(): void {
    this.router.navigate(['/tickets/crear']);
  }

  editarTicket(card: TicketKanbanCardResponse, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/tickets/editar', card.id]);
  }

  /**
   * Archiva la tarjeta (V180): sale del kanban y queda en Tickets → "Archivados".
   * Inc 8: confirma con el mismo ConfirmDialog que la vista Lista (antes `confirm()` nativo).
   */
  async archivarTicket(card: TicketKanbanCardResponse, event: Event): Promise<void> {
    event.stopPropagation();
    if (!card.id) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Archivar ticket',
      message: `¿Archivar el ticket ${card.ticketNumber}? Podrás consultarlo en "Archivados".`,
      confirmText: 'Archivar'
    });
    if (!ok) return;

    this.ticketsApi
      .archiveTicket(card.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.success('Ticket archivado');
          this.cargarKanban();
        },
        error: (err) => {
          this.toast.error({
            title: 'No se archivó',
            message: err.error?.message || 'Error al archivar el ticket'
          });
        }
      });
  }

  /** Inc 8: misma confirmación, mensaje y manejo de errores que `eliminarTicket` de la vista Lista. */
  async eliminarTicket(card: TicketKanbanCardResponse, event: Event): Promise<void> {
    event.stopPropagation();
    if (!card.id) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar ticket',
      message: `¿Está seguro de eliminar el ticket "${card.subject}"?`
    });
    if (!ok) return;

    this.ticketsApi
      .deleteTicket(card.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.deleted('Ticket');
          this.cargarKanban();
        },
        error: (err) => {
          this.toast.error({
            title: 'No se pudo eliminar',
            message: err.error?.message || 'Error al eliminar el ticket'
          });
        }
      });
  }

  // ===== Modal =====
  verDetalleModal(card: TicketKanbanCardResponse): void {
    if (!card.id) return;
    this.ticketsApi
      .getTicketById(card.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ticket) => {
          this.selectedTicket = ticket;
          this.showDetailModal = true;
        },
        error: () => {
          this.toast.error({ title: 'Error', message: 'No se pudo cargar el ticket' });
        }
      });
  }

  verDetalle(card: TicketKanbanCardResponse): void {
    this.verDetalleModal(card);
  }

  cerrarModalDetalle(): void {
    this.showDetailModal = false;
    this.selectedTicket = null;
  }
}
